import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { io as socketIo } from "socket.io-client";
import { fetchCannedReply, fetchConversation, fetchEmailBody, fetchEmails, replyToEmail } from "../services/emailService";
import { getAuthToken } from "../services/http";
import { CATEGORY_COLORS, getCategoryTranslation } from "./CategoriesPage";
import { filterEmails } from "../utils/search";
import AttachmentList from "../components/AttachmentList";
import EmailAttachments from "../components/EmailAttachments";

function InboxCategoryBadge({ category }) {
  const { i18n } = useTranslation();
  const palette = CATEGORY_COLORS[category] || { badge: "#95a5a6", text: "#fff" };
  return (
    <span
      className="inbox-category-badge"
      style={{ background: palette.badge, color: palette.text }}
    >
      {getCategoryTranslation(category, i18n.language)}
    </span>
  );
}

function BodyRenderer({ body }) {
  const iframeRef = useRef(null);
  const { t } = useTranslation();
  const isHtml = Boolean(body) && /<[^>]+>/.test(body);
  const htmlDoc = isHtml && !/<html[\s>]/i.test(body)
    ? `<!doctype html><html><body>${body}</body></html>`
    : body;

  function onLoad() {
    if (!iframeRef.current) return;
    try {
      const h = iframeRef.current.contentDocument?.body?.scrollHeight;
      if (h) iframeRef.current.style.height = Math.min(h + 30, 600) + "px";
    } catch (_) {}
  }

  if (isHtml) {
    return (
      <iframe
        ref={iframeRef}
        srcDoc={htmlDoc}
        sandbox="allow-same-origin"
        onLoad={onLoad}
        className="email-html-frame"
        title="Email content"
      />
    );
  }
  return <pre className="body">{body || t('categories.bodyNotAvailable')}</pre>;
}

// With REACT_APP_API_URL="/api" (production, same origin) this is undefined:
// socket.io then connects to the page's own host.
const SOCKET_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "") || undefined
  : "http://localhost:4000";

const LS_KEY = "safba_read_ids";

function loadReadIds() {
  try { return new Set(JSON.parse(localStorage.getItem(LS_KEY) || "[]")); }
  catch { return new Set(); }
}

function saveReadIds(set) {
  try { localStorage.setItem(LS_KEY, JSON.stringify([...set])); } catch {}
}

function htmlToTextFE(html) {
  if (!html) return "";
  const s = html.trim();
  if (!s.startsWith("<")) return s;
  return s
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function normalizeSubjectFE(subject) {
  return (subject || "")
    .replace(/^(\s*(Re|RE|Fwd|FWD|Fw|FW|Tr|TR)\s*:\s*)+/g, "")
    .trim()
    .toLowerCase();
}

function extractAddrFE(from) {
  const m = (from || "").match(/<([^>]+)>/);
  if (m) return m[1].trim().toLowerCase();
  if ((from || "").includes("@")) return (from || "").trim().toLowerCase();
  return (from || "").toLowerCase();
}

function groupThreads(emails) {
  const threads = new Map();
  for (const email of emails) {
    const key = `${extractAddrFE(email.from)}|${normalizeSubjectFE(email.subject)}`;
    const existing = threads.get(key);
    if (!existing || new Date(email.date) > new Date(existing.date)) {
      threads.set(key, email);
    }
  }
  return Array.from(threads.values()).sort((a, b) => new Date(b.date) - new Date(a.date));
}

function formatDateTime(dateStr, lang) {
  const date = new Date(dateStr);
  if (lang === "fr") {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } else {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }
}

export default function EmailPage() {
  const { t, i18n } = useTranslation();
  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const readIdsRef = useRef(loadReadIds());
  const [conversation, setConversation] = useState([]);
  const [pagingLoading, setPagingLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const pageCacheRef = useRef(new Map());
  const conversationCacheRef = useRef(new Map());
  const conversationRequestSeqRef = useRef(0);
  const inboxRequestSeqRef = useRef(0);
  const firstLoadDoneRef = useRef(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [message, setMessage] = useState("");
  const messageRef = useRef(message);
  messageRef.current = message;
  const [suggestionMeta, setSuggestionMeta] = useState(null);
  const [suggestionError, setSuggestionError] = useState("");
  const realtimeEmailIdsRef = useRef(new Set());
  const suggestionCacheRef = useRef(new Map());
  const autoFillByEmailRef = useRef(new Map());
  const draftByEmailRef = useRef(new Map());
  const previousSelectedIdRef = useRef(null);

  const updateDraftForEmail = useCallback((emailId, nextMessage) => {
    if (!emailId) return;
    draftByEmailRef.current.set(String(emailId), nextMessage);
  }, []);

  const clearDraftForEmail = useCallback((emailId) => {
    if (!emailId) return;
    draftByEmailRef.current.delete(String(emailId));
    autoFillByEmailRef.current.delete(String(emailId));
  }, []);

  const applyMessageForEmail = useCallback((emailId, nextMessage) => {
    setMessage(nextMessage);
    updateDraftForEmail(emailId, nextMessage);
  }, [updateDraftForEmail]);

  const applyInboxResult = useCallback((result, fallbackPage) => {
    const data = result.items || [];
    const nextPage = result.page || fallbackPage;
    setEmails(data);
    setPage(nextPage);
    setTotalPages(result.totalPages || 1);
    setSelected((current) => {
      if (!data.length) return null;
      if (!current) return data[0];
      return data.find((email) => email.id === current.id) || data[0];
    });
  }, []);

  const loadEmails = useCallback(async (targetPage = page, options = {}) => {
    const { forceSync = false } = options;
    const requestId = ++inboxRequestSeqRef.current;

    const cached = pageCacheRef.current.get(targetPage);
    if (cached && !forceSync) {
      applyInboxResult(cached, targetPage);
    }

    if (targetPage !== page) setPagingLoading(true);

    try {
      const result = await fetchEmails(targetPage, pageSize, { forceSync });
      if (requestId !== inboxRequestSeqRef.current) {
        return;
      }
      pageCacheRef.current.set(result.page || targetPage, result);
      applyInboxResult(result, targetPage);
    } catch (e) {
      console.error("Inbox refresh failed:", e.message);
    } finally {
      if (requestId === inboxRequestSeqRef.current) {
        setPagingLoading(false);
      }
    }
  }, [applyInboxResult, page, pageSize]);

  const loadConversation = useCallback(async (emailId) => {
    const requestId = ++conversationRequestSeqRef.current;
    if (!emailId) {
      setConversation([]);
      return;
    }

    const cached = conversationCacheRef.current.get(emailId);
    if (cached) {
      setConversation(cached);
    }

    setConversationLoading(true);
    try {
      const result = await fetchConversation(emailId);
      if (requestId !== conversationRequestSeqRef.current) return;
      const messages = result.messages || [];
      setConversation(messages);
      conversationCacheRef.current.set(emailId, messages);
    } catch (e) {
      console.error("Conversation load failed:", e.message);
    } finally {
      if (requestId === conversationRequestSeqRef.current) setConversationLoading(false);
    }
  }, []);

  async function onSend(e) {
    e.preventDefault();

    if (!selected) {
      alert(t('email.selectEmailFirst'));
      return;
    }

    try {
      setSending(true);
      await replyToEmail({ emailId: selected.id, text: message });
      alert(t('email.replySent'));
      clearDraftForEmail(selected.id);
      setMessage("");
      setSuggestionMeta(null);
      conversationCacheRef.current.delete(selected.id);
      await loadConversation(selected.id);
    } catch (e2) {
      alert(e2.message);
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    if (!firstLoadDoneRef.current) {
      firstLoadDoneRef.current = true;
      loadEmails(1, { forceSync: false });
      return;
    }

    loadEmails(page, { forceSync: false });
  }, [loadEmails, page]);

  useEffect(() => {
    const socket = socketIo(SOCKET_URL, { transports: ["polling", "websocket"], auth: { token: getAuthToken() } });

    socket.on("new-email", (data) => {
      pageCacheRef.current.clear();
      const incoming = data?.emails || [];
      for (const email of incoming) {
        if (email?.id !== undefined && email?.id !== null) {
          realtimeEmailIdsRef.current.add(String(email.id));
        }
      }
      if (incoming.length > 0 && page === 1) {
        setEmails((prev) => {
          const ids = new Set(prev.map((e) => e.id));
          const fresh = incoming.filter((e) => !ids.has(e.id));
          if (!fresh.length) return prev;
          const merged = [...fresh, ...prev];
          setSelected((cur) => cur || fresh[0]);
          return merged;
        });
      } else if (page === 1) {
        loadEmails(1, { forceSync: false });
      } else {
        setPage(1);
      }
    });

    return () => socket.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => loadEmails(page, { forceSync: false }), 60_000);
    return () => clearInterval(id);
  }, [loadEmails, page]);

  useEffect(() => {
    if (selected?.id) {
      setConversation([]);
      loadConversation(selected.id);
    } else {
      setConversation([]);
    }
  }, [selected, loadConversation]);

  useEffect(() => {
    const previousSelectedId = previousSelectedIdRef.current;
    if (previousSelectedId && previousSelectedId !== selected?.id) {
      updateDraftForEmail(previousSelectedId, messageRef.current);
    }

    previousSelectedIdRef.current = selected?.id ? String(selected.id) : null;

    const nextDraft = selected?.id ? draftByEmailRef.current.get(String(selected.id)) || "" : "";
    setMessage(nextDraft);
    setSuggestionMeta(null);
    setSuggestionError("");
  }, [selected?.id, i18n.language, updateDraftForEmail]);

  useEffect(() => {
    if (!selected) {
      setMessage("");
      setSuggestionMeta(null);
      return;
    }
    let cancelled = false;
    const selectedDateMs = new Date(selected.date || 0).getTime();
    const alreadyReplied = Boolean(selected.autoReplied) || conversation.some((msg) => {
      if (msg.direction !== "sent") return false;
      const sentDateMs = new Date(msg.date || 0).getTime();
      if (!Number.isFinite(sentDateMs)) return false;
      return sentDateMs > selectedDateMs;
    });
    const selectedKey = String(selected.id);
    const bodyText = htmlToTextFE(selected.text || "").slice(0, 500);
    const cacheKey = `${selectedKey}:${i18n.language}:${bodyText}`;
    const cachedSuggestion = suggestionCacheRef.current.get(cacheKey);
    const savedDraft = draftByEmailRef.current.get(selectedKey) || "";
    const previousAutoFill = autoFillByEmailRef.current.get(selectedKey) || "";
    const hasUserEditedDraft = Boolean(savedDraft.trim()) && savedDraft !== previousAutoFill;

    if (cachedSuggestion) {
      setSuggestionMeta({ ...cachedSuggestion.meta, autoSent: alreadyReplied });

      if (alreadyReplied || !cachedSuggestion.meta.shouldReply) {
        applyMessageForEmail(selectedKey, savedDraft);
      } else if (["suggest", "auto"].includes(cachedSuggestion.meta.action) && cachedSuggestion.suggestedText.trim()) {
        if (hasUserEditedDraft) {
          applyMessageForEmail(selectedKey, savedDraft);
        } else {
        autoFillByEmailRef.current.set(selectedKey, cachedSuggestion.suggestedText);
        applyMessageForEmail(selectedKey, cachedSuggestion.suggestedText);
        }
      } else if (savedDraft.trim()) {
        applyMessageForEmail(selectedKey, savedDraft);
      }
    }

    if (!bodyText.trim()) {
      return;
    }

    // Fetch at most once per selected email/language and reuse the cached result.
    if (cachedSuggestion) {
      return;
    }

    setSuggestionMeta(null);
    setSuggestionError("");
    fetchCannedReply({
      subject: selected.subject || "",
      body: bodyText,
      language: "auto",
    })
      .then((data) => {
        if (cancelled) return;

        const confidence = Number(data?.classification?.confidence || 0);
        const confidencePct = Number((confidence * 100).toFixed(1));
        const source = data?.classification?.source || "unknown";
        const shouldReply = data?.should_reply !== false;
        const action = data?.action || "manual";
        const suggestedText = data?.augmented_response || data?.response?.text || "";
        const shouldAutoSend = shouldReply && action === "auto";
        const skipReason = data?.skip_reason || "";
        const previousAutoFill = autoFillByEmailRef.current.get(selectedKey) || "";
        const hasUserEditedDraft = Boolean(savedDraft.trim()) && savedDraft !== previousAutoFill;

        if (alreadyReplied || !shouldReply) {
          applyMessageForEmail(selectedKey, savedDraft);
        } else if (["suggest", "auto"].includes(action) && suggestedText.trim()) {
          if (hasUserEditedDraft) {
            applyMessageForEmail(selectedKey, savedDraft);
          } else {
          setMessage((prev) => {
            const canReplace = !prev.trim() || prev === previousAutoFill;
            if (!canReplace) return prev;
            autoFillByEmailRef.current.set(selectedKey, suggestedText);
            updateDraftForEmail(selectedKey, suggestedText);
            return suggestedText;
          });
          }
        }
        const nextSuggestion = {
          confidencePct,
          source,
          shouldReply,
          action,
          skipReason,
          shouldAutoSend,
          autoSent: alreadyReplied,
          autoError: "",
        };

        suggestionCacheRef.current.set(cacheKey, {
          meta: nextSuggestion,
          suggestedText,
        });
        setSuggestionMeta(nextSuggestion);
      })
      .catch((error) => {
        if (!cancelled) {
          setSuggestionError(
            error.name === "AbortError"
              ? "La génération de la suggestion a dépassé le délai."
              : "La suggestion est temporairement indisponible."
          );
        }
      })
      .finally(() => {});
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, selected?.text, conversation, i18n.language]);

  useEffect(() => {
    if (!selected?.id || selected.text) return;
    let cancelled = false;
    fetchEmailBody(selected.id)
      .then((body) => {
        if (!cancelled && body) {
          setSelected((prev) =>
            prev?.id === selected.id ? { ...prev, text: body } : prev
          );
          setEmails((prev) =>
            prev.map((e) => (e.id === selected.id ? { ...e, text: body } : e))
          );
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const conversationTimeline = useMemo(() => {
    if (!selected) return [];

    const selectedAsMessage = {
      id: selected.id,
      direction: "received",
      from: selected.from,
      subject: selected.subject,
      text: selected.text,
      date: selected.date,
    };

    const hasSelected = conversation.some(
      (msg) => String(msg.id) === String(selected.id)
    );

    const merged = hasSelected ? [...conversation] : [...conversation, selectedAsMessage];
    merged.sort((a, b) => new Date(a.date) - new Date(b.date));
    return merged;
  }, [conversation, selected]);

  const filteredEmails = useMemo(() => {
    return filterEmails(emails, searchTerm);
  }, [emails, searchTerm]);

  const filteredThreads = useMemo(() => {
    return groupThreads(filteredEmails);
  }, [filteredEmails]);

  const filteredTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredThreads.length / pageSize));
  }, [filteredThreads.length, pageSize]);

  const isSearching = searchTerm.trim().length > 0;
  const visibleThreads = useMemo(() => {
    if (!isSearching) return filteredThreads;
    return filteredThreads.slice((page - 1) * pageSize, page * pageSize);
  }, [filteredThreads, isSearching, page, pageSize]);
  const displayTotalPages = isSearching ? filteredTotalPages : totalPages;

  return (
    <div className="email-page">
      <div className="topbar">
        <h2>{t('email.inbox')}</h2>
        <input
          type="text"
          className="search-input"
          placeholder={t('email.search')}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="layout">
        <div className="list-panel">
          {visibleThreads.length === 0 ? (
            <p className="no-results">{t('email.noResults')}</p>
          ) : (
            <>
              {visibleThreads.map((email) => {
            const isUnread = !readIdsRef.current.has(email.id);
            const isNew = isUnread && (Date.now() - new Date(email.date).getTime()) < 86_400_000;
            return (
              <div
                key={email.id}
                className={`email-item ${selected?.id === email.id ? "active" : ""} ${isUnread ? "email-item--unread" : ""}`}
                onClick={() => {
                  readIdsRef.current.add(email.id);
                  saveReadIds(readIdsRef.current);

                  if (selected && String(selected.id) !== String(email.id)) {
                    clearDraftForEmail(selected.id);
                    setMessage("");
                    setSuggestionMeta(null);
                  }

                  setSelected(email);
                }}
              >
                <div className="email-item-top">
                  <div className="from">{email.from}</div>
                  <div className="email-item-badges">
                    {email.hasAttachments && <span className="inbox-new-badge">{t("attachments.title")}</span>}
                    {isNew && <span className="inbox-new-badge">{t('email.new')}</span>}
                    {email.autoReplied && email.category && <InboxCategoryBadge category={email.category} />}
                  </div>
                </div>
                <div className="subject">{email.subject}</div>
                <div className="date">{formatDateTime(email.date, i18n.language)}</div>
              </div>
            );
          })}

          {displayTotalPages > 0 && (
            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || pagingLoading}
              >
                {t('email.previous')}
              </button>
              <span>
                {t('email.page', { current: page, total: displayTotalPages })}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(displayTotalPages, p + 1))}
                disabled={page >= displayTotalPages || pagingLoading}
              >
                {t('email.next')}
              </button>
            </div>
          )}
            </>
          )}
        </div>

        <div className="viewer-panel">
          {selected ? (
            <>
              <h3>{selected.subject}</h3>
              <p><strong>{t('email.from')}</strong> {selected.from}</p>
              <p><strong>{t('email.date')}</strong> {formatDateTime(selected.date, i18n.language)}</p>
              <EmailAttachments emailId={selected.id} />
              <hr />

              <h4>{t('email.conversation')}</h4>
              {conversationLoading && conversation.length === 0 ? (
                <p>{t('email.loadingConversation')}</p>
              ) : (
                <div className="conversation-list">
                  {conversationTimeline.map((msg) => (
                    <div
                      key={msg.id}
                      className={`conversation-item ${msg.direction === "sent" ? "sent" : "received"}`}
                    >
                      <div className="conversation-meta">
                        <strong>{msg.direction === "sent" ? t('email.you') : msg.from}</strong>
                        <span>{formatDateTime(msg.date, i18n.language)}</span>
                      </div>
                      <div className="conversation-subject">{msg.subject}</div>
                      <div className="conversation-body">
                        <BodyRenderer body={msg.text} />
                        {String(msg.id) !== String(selected.id) && <AttachmentList attachments={msg.attachments} />}
                        {msg.attachmentsError && <p role="alert">{t("attachments.loadError")}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p>{t('email.selectEmail')}</p>
          )}

          <form className="reply-form" onSubmit={onSend}>
            <h4>{t('email.reply')}</h4>
            {suggestionError && (
              <p role="status" style={{ fontSize: "0.85em", color: "#9a3b24", marginTop: 0 }}>
                {suggestionError}
              </p>
            )}
            {suggestionMeta && (
              <p style={{ fontSize: "0.85em", color: suggestionMeta.shouldAutoSend ? "#1f7a1f" : "#666", marginTop: 0 }}>
                {!suggestionMeta.shouldReply
                  ? suggestionMeta.skipReason === "below-confidence-threshold"
                    ? `Confiance faible: ${suggestionMeta.confidencePct}% (${suggestionMeta.source}) - sous le seuil, aucune suggestion affichée`
                    : `Confiance: ${suggestionMeta.confidencePct}% (${suggestionMeta.source}) - aucune réponse nécessaire`
                  : suggestionMeta.action === "auto"
                    ? suggestionMeta.autoSent
                      ? `Confiance: ${suggestionMeta.confidencePct}% (${suggestionMeta.source}) - Réponse automatique déjà envoyée`
                      : `Confiance: ${suggestionMeta.confidencePct}% (${suggestionMeta.source}) - Seuil auto atteint; brouillon préparé à vérifier avant envoi`
                    : suggestionMeta.action === "suggest"
                      ? `Confiance: ${suggestionMeta.confidencePct}% (${suggestionMeta.source}) - À éditer avant envoi`
                      : `Confiance: ${suggestionMeta.confidencePct}% (${suggestionMeta.source}) - Non envoyé, révision manuelle`}
                {suggestionMeta.autoSent ? " - Réponse envoyée automatiquement." : ""}
                {suggestionMeta.autoError ? ` - Erreur auto-envoi: ${suggestionMeta.autoError}` : ""}
              </p>
            )}
            <textarea
              placeholder={t('email.messagePlaceholder')}
              rows={6}
              value={message}
              onChange={(e) => applyMessageForEmail(selected?.id, e.target.value)}
              required
            />
            <button type="submit" disabled={!selected || sending}>
              {sending ? t('email.sending') : t('email.sendReply')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
